import { motion } from "framer-motion"
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Twitter, Star, Heart, Award, ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { useLanguage } from "../contexts/LanguageContext"

const Footer = () => {
    const { t } = useLanguage()
    const currentYear = new Date().getFullYear()

    return (
        <footer className="relative bg-gray-900 text-white overflow-hidden">
            {/* Enhanced Background Decorations */}
            <div className="absolute inset-0">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-2xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-2xl" />
                <div className="absolute top-1/3 right-1/3 w-64 h-64 bg-secondary/3 rounded-full blur-xl" />

                {/* Subtle Pattern Overlay */}
                <div className="absolute inset-0 opacity-5">
                    <div className="w-full h-full" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                    }}></div>
                </div>
            </div>

            {/* Newsletter Section */}
            <div className="relative border-b border-white/10">
                <div className="container mx-auto max-w-7xl px-6 py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        {/* Badge */}
                        <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Heart className="w-4 h-4" />
                            <span>{t('join.stayConnected')}</span>
                        </div>

                        <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 leading-tight">
                            {t('join.culinaryJourney')}
                        </h2>
                        <p className="text-xl text-gray-300 mb-8 leading-relaxed font-light">
                            {t('join.getExclusiveOffers')}
                        </p>

                        {/* Newsletter Form */}
                        <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto mb-8">
                            <input
                                type="email"
                                placeholder={t('join.enterYourEmail')}
                                className="flex-1 px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                            />
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-300 flex items-center space-x-2 justify-center shadow-lg hover:shadow-xl"
                            >
                                <span>{t('join.subscribe')}</span>
                                <ArrowRight className="w-5 h-5" />
                            </motion.button>
                        </div>

                        {/* Trust Indicators */}
                        <div className="flex justify-center items-center space-x-8 flex-wrap gap-4">
                            <div className="flex items-center space-x-2 text-gray-400">
                                <Star className="w-5 h-5 text-yellow-500" />
                                <span className="text-sm">{t('join.rating')}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-400">
                                <Award className="w-5 h-5 text-primary-500" />
                                <span className="text-sm">{t('join.awardWinner')}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-400">
                                <Heart className="w-5 h-5 text-accent-500" />
                                <span className="text-sm">{t('join.happyCustomers')}</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            <div className="relative container mx-auto max-w-7xl px-6 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    {/* Restaurant Info - Enhanced */}
                    <div className="lg:col-span-2">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            {/* Logo Section */}
                            <div className="flex items-center space-x-4 mb-6">
                                <img src="images/logo.png" alt={t('alt.logo')} className="h-12 lg:h-14 rounded-full object-cover" />
                                <div>
                                    <h3 className="text-xl font-serif font-bold text-white">
                                        restaurant
                                    </h3>
                                    <p className="text-primary-400 text-sm font-medium">{t('join.diningEntertainmentHub')}</p>
                                </div>
                            </div>

                            <p className="text-gray-300 mb-8 leading-relaxed text-base font-light">
                                {t('footer.description')}
                            </p>

                            {/* Enhanced Social Media */}
                            <div className="flex items-center space-x-4 mb-8">
                                <span className="text-gray-400 font-medium">{t('join.followUs')}</span>
                                <div className="flex space-x-3">
                                    <motion.a
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        href="https://www.facebook.com/restaurant.goma"
                                        className="group w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl"
                                        aria-label={t('aria.facebook')}
                                    >
                                        <Facebook className="w-5 h-5 text-white" />
                                    </motion.a>
                                    <motion.button
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        className="group w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl"
                                        aria-label={t('aria.instagram')}
                                        onClick={() => window.open('https://instagram.com', '_blank')}
                                    >
                                        <Instagram className="w-5 h-5 text-white" />
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        className="group w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl"
                                        aria-label={t('aria.twitter')}
                                        onClick={() => window.open('https://twitter.com', '_blank')}
                                    >
                                        <Twitter className="w-5 h-5 text-white" />
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Enhanced Quick Links */}
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <h4 className="text-xl font-semibold mb-6 text-white flex items-center">
                                <div className="w-1 h-6 bg-gradient-to-b from-primary-500 to-accent-500 rounded-full mr-3"></div>
                                {t('footer.quickLinks')}
                            </h4>
                            <ul className="space-y-4">
                                {[
                                    { to: "/", label: t('nav.home') },
                                    { to: "/menu", label: t('nav.menu') },
                                    { to: "/about", label: t('nav.about') },
                                    { to: "/gallery", label: t('nav.gallery') },
                                    { to: "/contact", label: t('nav.contact') },
                                    { to: "/reservation", label: t('nav.reservations') }
                                ].map((link, index) => (
                                    <motion.li
                                        key={link.to}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                                        viewport={{ once: true }}
                                    >
                                        <Link
                                            to={link.to}
                                            className="group flex items-center space-x-2 text-gray-300 hover:text-primary-400 transition-all duration-300 font-medium"
                                        >
                                            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300 text-primary-500" />
                                            <span>{link.label}</span>
                                        </Link>
                                    </motion.li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>

                    {/* Enhanced Contact Info */}
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <h4 className="text-xl font-semibold mb-6 text-white flex items-center">
                                <div className="w-1 h-6 bg-gradient-to-b from-accent-500 to-secondary-500 rounded-full mr-3"></div>
                                {t('footer.contactInfo')}
                            </h4>
                            <div className="space-y-6">
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: 0.3 }}
                                    viewport={{ once: true }}
                                    className="group"
                                >
                                    <div className="flex items-start space-x-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300">
                                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <MapPin className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <p className="text-gray-300 font-medium leading-relaxed">
                                                av. Lyn Lusi 058,<br />
                                                Goma,<br />
                                                Congo-Kinshasa
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: 0.4 }}
                                    viewport={{ once: true }}
                                >
                                    <a
                                        href="tel:+243971566525"
                                        className="group flex items-center space-x-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300"
                                    >
                                        <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Phone className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wider">Call Us</p>
                                            <p className="text-gray-300 hover:text-primary-400 transition-colors font-medium">
                                                +243 971 566 525
                                            </p>
                                        </div>
                                    </a>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <a
                                        href="mailto:contact@ilrasentino.com"
                                        className="group flex items-center space-x-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300"
                                    >
                                        <div className="w-10 h-10 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Mail className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wider">Email Us</p>
                                            <p className="text-gray-300 hover:text-primary-400 transition-colors font-medium">
                                                contact@ilrasentino.com
                                            </p>
                                        </div>
                                    </a>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Enhanced Opening Hours */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="mt-16 pt-12 border-t border-white/10"
                >
                    <div className="text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                            viewport={{ once: true }}
                            className="inline-flex items-center space-x-3 bg-white/5 px-6 py-3 rounded-2xl border border-white/10 mb-8"
                        >
                            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
                                <Clock className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-left">
                                <h4 className="text-xl font-semibold text-white">
                                    {t('footer.openingHours')}
                                </h4>
                                <p className="text-gray-400 text-sm">We're here to serve you</p>
                            </div>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                            {[
                                { day: t('footer.days.sunday'), hours: t('footer.hours'), isSpecial: false },
                                { day: t('footer.days.monday'), hours: t('footer.hours'), isSpecial: false },
                                { day: t('footer.days.tuesday'), hours: t('footer.hours'), isSpecial: false },
                                { day: t('footer.days.wednesday'), hours: t('footer.hours'), isSpecial: false },
                                { day: t('footer.days.thursday'), hours: t('footer.hours'), isSpecial: false },
                                { day: t('footer.days.friday'), hours: t('footer.hours'), isSpecial: false },
                                { day: t('footer.days.saturday'), hours: t('footer.hours.saturday'), isSpecial: true }
                            ].map((item, index) => (
                                <motion.div
                                    key={item.day}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                                    viewport={{ once: true }}
                                    className={`relative p-4 rounded-2xl transition-all duration-300 hover:scale-105 ${item.isSpecial
                                        ? 'bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/20'
                                        : 'bg-white/5 hover:bg-white/10 border-white/10'
                                        } border`}
                                >
                                    <span className="font-semibold text-white block mb-2 text-sm">
                                        {item.day}
                                    </span>
                                    <span className={`text-sm font-medium ${item.isSpecial ? 'text-amber-400' : 'text-gray-300'
                                        }`}>
                                        {item.hours}
                                    </span>
                                    {item.isSpecial && (
                                        <div className="absolute top-2 right-2 w-2 h-2 bg-amber-500 rounded-full"></div>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Enhanced Copyright */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="mt-16 pt-8 border-t border-white/10"
                >
                    <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                        <div className="flex items-center space-x-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
                                <Heart className="w-4 h-4 text-white" />
                            </div>
                            <p className="text-gray-400 font-medium">
                                © {currentYear} restaurant. {t('footer.copyright')}
                            </p>
                        </div>

                        <div className="flex items-center space-x-6 text-sm text-gray-400">
                            <button className="hover:text-primary-400 transition-colors cursor-pointer">{t('footer.privacyPolicy')}</button>
                            <button className="hover:text-primary-400 transition-colors cursor-pointer">{t('footer.termsOfService')}</button>
                            <button className="hover:text-primary-400 transition-colors cursor-pointer">{t('footer.cookiePolicy')}</button>
                        </div>
                    </div>

                    {/* Final CTA */}
                    <div className="text-center mt-8 pt-8 border-t border-white/5">
                        <p className="text-gray-500 text-sm">
                            Made with <Heart className="w-4 h-4 inline text-red-500 mx-1" /> by{' '}
                            <a
                                href="https://upper-z.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-400 hover:text-primary-300 font-medium transition-colors duration-300 hover:underline"
                            >
                                Upperz
                            </a>
                            {' '}for food lovers in Goma
                        </p>
                    </div>
                </motion.div>
            </div>
        </footer>
    )
}

export default Footer
